import { captureException } from '@ifixit/sentry';
import { useStableCallback } from '@ifixit/ui/hooks/use-stable-callback';
import { type ComponentType, useEffect, useState } from 'react';
import { ReadinessGateHold } from 'Shared/react-mount-tracker';
import { useHasPainted } from 'Shared/use_has_painted';

interface MountOnFirstOpenProps<Props extends object> {
   isOpen: boolean;
   /**
    * The import() of a module whose default export is the sheet. It gets
    * `canOpen` on top of `props`: false for the first paint, then true.
    */
   load: () => Promise<{ default: ComponentType<Props & { canOpen: boolean }> }>;
   props: Props;
   /**
    * The fetch failed: close whatever asked to open, so the next open
    * fetches again instead of waiting on a sheet that never comes.
    */
   onLoadFailed: () => void;
}

/**
 * Renders a fetched sheet from the first time `isOpen` is true, and keeps it
 * mounted after so it plays its own close and reopen. Nothing is fetched
 * before that. While the fetch runs, the WebDriver readiness gate waits for
 * it. A failed fetch renders nothing and calls `onLoadFailed`; the next open
 * fetches again. (React.lazy would throw the failure up to the page's root,
 * which unmounts the whole header, and would keep the rejected fetch.)
 *
 * The loaded component gets `canOpen`, false until the page has painted it
 * once: a Sheet that renders closed first plays its slide-in and moves focus
 * in, which one mounted already open doesn't. A modal dialog that moves focus
 * in on its own (the pro lead form) can ignore it.
 */
export function MountOnFirstOpen<Props extends object>({
   isOpen,
   load,
   props,
   onLoadFailed,
}: MountOnFirstOpenProps<Props>) {
   // One request per time `isOpen` turns true. A failed fetch ends its
   // request, so only the next open fetches again, even when the caller's
   // onLoadFailed leaves `isOpen` true.
   const [request, setRequest] = useState({ count: isOpen ? 1 : 0, hasFailed: false });
   const [wasOpen, setWasOpen] = useState(isOpen);
   if (isOpen !== wasOpen) {
      setWasOpen(isOpen);
      if (isOpen) setRequest(({ count }) => ({ count: count + 1, hasFailed: false }));
   }
   const [Sheet, setSheet] = useState<ComponentType<Props & { canOpen: boolean }> | null>(null);
   const handleLoadFailed = useStableCallback(onLoadFailed);
   const isLoading = request.count > 0 && !request.hasFailed && !Sheet;

   useEffect(() => {
      if (!isLoading) return;
      let isCurrent = true;
      void (async () => {
         try {
            const { default: loaded } = await load();
            if (isCurrent) setSheet(() => loaded);
         } catch (error) {
            if (!isCurrent) return;
            console.error('Could not load a sheet', error);
            // Sentry already ignores a failed chunk fetch; this catches a
            // module that throws while it evaluates, which used to break the
            // page and reach Sentry through the global handler.
            captureException(error);
            setRequest(({ count }) => ({ count, hasFailed: true }));
            handleLoadFailed();
         }
      })();
      return () => {
         isCurrent = false;
      };
   }, [isLoading, request.count, load, handleLoadFailed]);

   if (Sheet) return <OpenAfterFirstPaint Sheet={Sheet} props={props} />;
   return isLoading ? <ReadinessGateHold /> : null;
}

function OpenAfterFirstPaint<Props extends object>({
   Sheet,
   props,
}: {
   Sheet: ComponentType<Props & { canOpen: boolean }>;
   props: Props;
}) {
   return <Sheet {...props} canOpen={useHasPainted()} />;
}

/**
 * Starts `load` early, on hover or focus, so the open usually finds it done.
 * A failure is dropped here: the open fetches again and reports it.
 */
export async function prefetch(load: () => Promise<unknown>) {
   try {
      await load();
   } catch {
      // MountOnFirstOpen retries and reports.
   }
}
